import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/macro';
import { MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import NgpSelect from '../../../../../form/NgpSelect';
import NgpTextField from '../../../../../form/NgpTextField';
import {
    CALL_VIA_EXTENSION,
    CALL_VIA_OPTIONS,
    CALL_VIA_PHONE_NUMBER,
    CALL_VIA_SIP_USER_ID,
} from '../../../../../panels/voip-test-now-panels/VoipTestConstants';
import { PLACEHOLDERS } from '../../../../../panels/voip-test-now-panels/VoipPulseTestConfigHelpers';
import { AUTO_COMPLETE_DEFAULT } from '../../../../../../utilities/FormUtilities';
import { SHOULD_DIRTY_TRUE } from '../../../ServiceTestsFormControlConstants';
import TestSubFormSection from '../TestFormSection/TestSubFormSection';
import { useNoPaddingLeftStyles } from '../../../../../form/FormStyles';

const useStyles = makeStyles(() => ({
    container: { marginTop: 24 },
    errorBorder: {
        border: '1px solid #BF3D56',
        borderRadius: 4,
        padding: '8px 16px 16px',
    },
    normalBorder: {
        border: '1px solid rgb(220, 220, 220)',
        borderRadius: 4,
        padding: '8px 16px 16px',
    },
    errorText: {
        color: '#BF3D56',
        marginTop: 4,
        fontSize: '0.75rem',
    },
    testSelect: {
        marginRight: '8px',

        '& .MuiSelect-select': {
            minWidth: 390,
        },
    },
}));

/**
 * Check whether both agents share the same primary call manager.
 */
function hasSameCallManager(callerAgent, calleeAgent) {
    const callerId = get(callerAgent, 'voipConfiguration.primaryCallManager._id');
    const calleeId = get(calleeAgent, 'voipConfiguration.primaryCallManager._id');
    if (!callerId || !calleeId) return false;
    return callerId === calleeId;
}

/**
 * Return the auto-fill value from the callee agent's voipConfiguration
 * based on the selected call-via head.
 */
function getAutoFillValue(calleeAgent, head) {
    switch (head) {
        case CALL_VIA_PHONE_NUMBER:
            return get(calleeAgent, 'voipConfiguration.phoneNumber', '');
        case CALL_VIA_EXTENSION:
            return get(calleeAgent, 'voipConfiguration.extension', '');
        default:
            return '';
    }
}

/**
 * Return the body field label/placeholder based on the selected head.
 */
function getBodyLabel(head) {
    switch (head) {
        case CALL_VIA_EXTENSION:
            return PLACEHOLDERS.EXTENSION;
        case CALL_VIA_PHONE_NUMBER:
        default:
            return PLACEHOLDERS.PHONE_NUMBER;
    }
}

/**
 * Callee Instructions section for VoIP Pulse test configuration.
 *
 * Renders a dropdown (Phone Number / Extension) and an input field that
 * auto-fills from the callee agent's voipConfiguration. Shows real-time
 * validation when head or body is empty.
 *
 * Visibility logic (same/different call manager, direct calling) is
 * handled by the parent component — this component simply renders when shown.
 */
export default function CalleeInstructionsSection({ callerAgent, calleeAgent }) {
    const classes = useStyles();
    const noPaddingLeftStyles = useNoPaddingLeftStyles();
    const { setValue, getValues, watch } = useFormContext();

    const isSameCM = hasSameCallManager(callerAgent, calleeAgent);

    // Watch form values for real-time validation and dynamic label
    const callViaHead = watch('testConfig.callVia.head', '');
    const callViaBody = watch('testConfig.callVia.body', '');

    const hasError = !callViaHead || !callViaBody;
    const placeholder = getBodyLabel(callViaHead);
    const availableOptions = isSameCM ? CALL_VIA_OPTIONS : [CALL_VIA_OPTIONS[0]];

    const handleChange = useCallback(
        (newHead, newBody) => {
            setValue('testConfig.callVia.head', newHead, SHOULD_DIRTY_TRUE);
            setValue('testConfig.callVia.body', newBody, {
                ...SHOULD_DIRTY_TRUE,
                shouldValidate: true, // Ensure trigger validate, clear“Value is required”
            });
            setValue('testConfig.callVia.useDirectCalling', false, SHOULD_DIRTY_TRUE);
        },
        [setValue]
    );

    // Initialize / synchronize form values when agents change.
    // Must NOT use shouldDirty on mount — setting values during initialization
    // should not mark the form dirty (user hasn't edited anything yet).
    useEffect(() => {
        let head = getValues('testConfig.callVia.body') || '';
        let body = getValues('testConfig.callVia.body') || '';
        const useDirectCalling = getValues('testConfig.callVia.useDirectCalling') || false;

        let changed = false;

        if (!isSameCM) {
            // Different call managers — force phone number mode
            head = CALL_VIA_PHONE_NUMBER;
            body = getAutoFillValue(calleeAgent, CALL_VIA_PHONE_NUMBER);
            changed = true;
        } else if (head === CALL_VIA_SIP_USER_ID) {
            // Legacy uid: head — fallback to phone number
            head = CALL_VIA_PHONE_NUMBER;
            body = getAutoFillValue(calleeAgent, CALL_VIA_PHONE_NUMBER);
            changed = true;
        } else if (!head) {
            // No head set yet — default to phone number with auto-fill
            head = CALL_VIA_PHONE_NUMBER;
            body = getAutoFillValue(calleeAgent, CALL_VIA_PHONE_NUMBER);
            changed = true;
        }

        // Only update form when values actually need correction.
        // Never use shouldDirty during initialization — dirty state should
        // only be set by explicit user interaction (handleHeadChange).
        if (changed || useDirectCalling !== false) {
            handleChange(head, body);
        }
    }, [callerAgent, calleeAgent, isSameCM, setValue, getValues, handleChange]);

    const handleHeadChange = useCallback(
        (event) => {
            const newHead = event.target.value;
            const newBody = getAutoFillValue(calleeAgent, newHead);
            handleChange(newHead, newBody);
        },
        [calleeAgent, handleChange]
    );

    return (
        <TestSubFormSection fieldsClassName={noPaddingLeftStyles.ngpFormFields}>
            <div className={classes.container}>
                <div className={hasError ? classes.errorBorder : classes.normalBorder}>
                    <TestSubFormSection title={<Trans>Callee Instructions</Trans>}>
                        <NgpSelect
                            fieldName="testConfig.callVia.head"
                            label={<Trans>Call this nPoint using the</Trans>}
                            required
                            className={classes.testSelect}
                            onChangeFunc={handleHeadChange}
                            disabled={!isSameCM}
                        >
                            {availableOptions.map((callVia) => (
                                <MenuItem key={callVia.name} value={callVia.value}>
                                    {callVia.name}
                                </MenuItem>
                            ))}
                        </NgpSelect>

                        <NgpTextField
                            fieldName="testConfig.callVia.body"
                            label={placeholder}
                            disabled={!callViaHead}
                            required
                            inputProps={{
                                size: 35,
                                maxLength: 150,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                    </TestSubFormSection>
                </div>
            </div>
            {hasError && (
                <Typography className={classes.errorText}>
                    <Trans>Callee Instructions are required</Trans>
                </Typography>
            )}
        </TestSubFormSection>
    );
}

CalleeInstructionsSection.propTypes = {
    callerAgent: PropTypes.object.isRequired,
    calleeAgent: PropTypes.object.isRequired,
};
